import type { TCommentWithAuthor, TError, TPostWithAuthor, TToggleReaction } from '@onesource/schemas';
import { useMutation, useQueryClient, type InfiniteData } from '@tanstack/react-query';

import axios from '../../axios';
import { ENTITY } from '../entity';

interface ToggleReactionInput extends TToggleReaction {
  currentReaction?: string | null;
}

export const useToggleReaction = () => {
  const queryClient = useQueryClient();

  return useMutation<void, TError, ToggleReactionInput>({
    mutationFn: async ({ currentReaction: _, ...data }) => {
      await axios.post('/community-hub/reactions/toggle', data);
    },
    onMutate: async ({ targetType, targetId, emoji, currentReaction }) => {
      const isRemoving = currentReaction === emoji;
      const newEmoji = isRemoving ? null : emoji;

      let countDelta = 0;
      if (isRemoving) {
        countDelta = -1;
      } else if (!currentReaction) {
        countDelta = 1;
      }

      if (targetType === 'post') {
        await queryClient.cancelQueries({ queryKey: [ENTITY, 'posts'] });

        const patchPost = (post: TPostWithAuthor): TPostWithAuthor => {
          if (post.id !== targetId) {
            return post;
          }
          return { ...post, userReaction: newEmoji, likeCount: Math.max(0, post.likeCount + countDelta) };
        };

        // Infinite feed queries
        queryClient.setQueriesData<InfiniteData<{ posts: TPostWithAuthor[]; total: number }>>(
          { queryKey: [ENTITY, 'posts', 'infinite'] },
          old => {
            if (!old) {
              return old;
            }
            return { ...old, pages: old.pages.map(page => ({ ...page, posts: page.posts.map(patchPost) })) };
          },
        );

        // Flat list queries
        queryClient.setQueriesData<TPostWithAuthor[]>(
          { queryKey: [ENTITY, 'posts'], predicate: q => !q.queryKey.includes('infinite') },
          old => (Array.isArray(old) ? old.map(patchPost) : old),
        );
      } else {
        await queryClient.cancelQueries({ queryKey: [ENTITY, 'comments'] });

        const patchComment = (comment: TCommentWithAuthor): TCommentWithAuthor => {
          const patched =
            comment.id === targetId
              ? { ...comment, userReaction: newEmoji, likeCount: Math.max(0, comment.likeCount + countDelta) }
              : comment;

          if (patched.replies?.length) {
            return {
              ...patched,
              replies: patched.replies.map(r =>
                r.id === targetId
                  ? { ...r, userReaction: newEmoji, likeCount: Math.max(0, r.likeCount + countDelta) }
                  : r,
              ),
            };
          }
          return patched;
        };

        queryClient.setQueriesData<TCommentWithAuthor[]>({ queryKey: [ENTITY, 'comments'] }, old =>
          Array.isArray(old) ? old.map(patchComment) : old,
        );
      }
    },
    onSettled: (_data, _err, { targetType, targetId }) => {
      queryClient.invalidateQueries({ queryKey: [ENTITY, targetType === 'post' ? 'posts' : 'comments'] });
      queryClient.invalidateQueries({ queryKey: [ENTITY, 'reactions', targetType, targetId] });
    },
  });
};
